import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  emptyPropertyListAdvancedFilters,
  isInvalidMoneyRange,
  pickAdvancedPropertyListFilters,
  sanitizePropertyListAdvancedFilters,
  type PropertyListAdvancedFilters,
  type PropertyListFilters,
} from "./propertiesListQuery";
import { PropertiesListFiltersForm } from "./PropertiesListFiltersForm";
import { usePropertyListFilterOptions } from "./usePropertyListFilterOptions";
import { useToast } from "../../hooks/toast";

type Props = {
  open: boolean;
  filters: PropertyListFilters;
  showConsultantFilter: boolean;
  onClose: () => void;
  onApply: (filters: PropertyListAdvancedFilters) => void;
};

export function PropertiesListFiltersModal({
  open,
  filters,
  showConsultantFilter,
  onClose,
  onApply,
}: Props) {
  const { cities, consultants } = usePropertyListFilterOptions(
    showConsultantFilter
  );
  const [draft, setDraft] = useState<PropertyListAdvancedFilters>(
    pickAdvancedPropertyListFilters(filters)
  );
  const [hasRentRangeError, setHasRentRangeError] = useState(false);
  const [hasAreaRangeError, setHasAreaRangeError] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    if (!open) return;
    setDraft(pickAdvancedPropertyListFilters(filters));
    setHasRentRangeError(false);
    setHasAreaRangeError(false);
  }, [open, filters]); 

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  function handleApply() {
    const rentInvalid = isInvalidMoneyRange(
      draft.minRentValue,
      draft.maxRentValue
    );
    const areaInvalid = isInvalidMoneyRange(draft.minArea, draft.maxArea);

    setHasRentRangeError(rentInvalid);
    setHasAreaRangeError(areaInvalid);

    if (rentInvalid || areaInvalid) {
      addToast({
        title: "O máximo deve ser maior que o mínimo",
        type: "error",
      });
      return;
    }

    onApply(sanitizePropertyListAdvancedFilters(draft));
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Fechar filtros"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="properties-filters-title"
        className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-visible rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2
            id="properties-filters-title"
            className="text-lg font-semibold text-slate-800"
          >
            Filtros
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100"
            aria-label="Fechar"
          > 
            <X className="h-5 w-5" />
          </button>
        </div>
        <PropertiesListFiltersForm
          draft={draft}
          cities={cities}
          consultants={consultants}
          showConsultantFilter={showConsultantFilter}
          hasRentRangeError={hasRentRangeError}
          hasAreaRangeError={hasAreaRangeError}
          onChange={setDraft}
          onRentRangeChange={() => setHasRentRangeError(false)}
          onAreaRangeChange={() => setHasAreaRangeError(false)}
        />

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => {
              setHasRentRangeError(false);
              setHasAreaRangeError(false);
              setDraft(emptyPropertyListAdvancedFilters());
            }}
            className="inline-flex h-[38px] items-center justify-center rounded-md border border-solid border-[#E2E8F0] bg-white px-4 text-sm font-medium text-slate-600 transition hover:border-[#CBD5E0] hover:bg-slate-50"
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="inline-flex h-[38px] items-center justify-center rounded-md border-0 bg-[#DD6435] px-4 text-sm font-medium text-white transition hover:bg-[#c4552b]"
          >
            Aplicar filtros
          </button>
        </div>
      </div>
    </div>
  );
}
